/**
 * Editor toolbar — mode switch, undo/redo, selection actions, grid/snap
 * toggles, save. Horizontal scroll keeps every control reachable on phones;
 * every control carries a web hover tooltip with its shortcut hint.
 *
 * @module FloorPlanEditorToolbar
 * @category Containers
 */

import { ScrollView, StyleSheet, View } from 'react-native';
import {
  Copy,
  Eye,
  Grid3x3,
  Hand,
  Magnet,
  MousePointer2,
  PenLine,
  Redo2,
  Ruler,
  Save,
  SquareDashed,
  Trash2,
  Undo2,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import FloorPlanToolButton from './ToolButton';

import { modKeyLabel } from '@src/floorplan/keyboard/shortcuts';
import { FLOOR_MODE, type ModeId } from '@src/floorplan/models/mode';
import { useFloorEngineStore } from '@src/floorplan/stores/useFloorEngineStore';

type Props = {
  /** Whether the viewer may edit the plan (gates mode switches, undo/redo, save). */
  canModify: boolean;
  /** Enables the Undo button. */
  canUndo: boolean;
  /** Enables the Redo button. */
  canRedo: boolean;
  /** Enables Duplicate/Delete (there is an active selection). */
  hasSelection: boolean;
  /** Fired when Undo is pressed. */
  onUndo: () => void;
  /** Fired when Redo is pressed. */
  onRedo: () => void;
  /** Fired when Delete is pressed. */
  onDelete: () => void;
  /** Fired when Duplicate is pressed. */
  onDuplicate: () => void;
  /** Fired when the Ruler (room size) button is pressed. */
  onRoomSize: () => void;
  /** Fired when Save is pressed. */
  onSave: () => void;
};

const MODE_ORDER: {
  id: ModeId;
  icon: typeof MousePointer2;
  hint?: string;
}[] = [
  { id: FLOOR_MODE.EDIT, icon: MousePointer2, hint: 'V' },
  { id: FLOOR_MODE.SELECTION, icon: SquareDashed, hint: 'M' },
  { id: FLOOR_MODE.DRAW, icon: PenLine },
  { id: FLOOR_MODE.PAN, icon: Hand, hint: 'H' },
  { id: FLOOR_MODE.VIEW, icon: Eye },
];

/**
 * Editor toolbar — mode switch, undo/redo, selection actions, grid/snap
 * toggles, save. Horizontally scrollable so every control stays reachable
 * on phones.
 *
 * @category Containers
 * @param canModify - Whether the viewer may edit the plan.
 * @param canUndo - Enables the Undo button.
 * @param canRedo - Enables the Redo button.
 * @param hasSelection - Enables Duplicate/Delete.
 * @param onUndo - Fired when Undo is pressed.
 * @param onRedo - Fired when Redo is pressed.
 * @param onDelete - Fired when Delete is pressed.
 * @param onDuplicate - Fired when Duplicate is pressed.
 * @param onRoomSize - Fired when the Ruler button is pressed.
 * @param onSave - Fired when Save is pressed.
 */
const FloorPlanEditorToolbar = ({
  canModify,
  canUndo,
  canRedo,
  hasSelection,
  onUndo,
  onRedo,
  onDelete,
  onDuplicate,
  onRoomSize,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const mode = useFloorEngineStore((s) => s.mode);
  const gridEnabled = useFloorEngineStore((s) => s.gridEnabled);
  const snapEnabled = useFloorEngineStore((s) => s.snapEnabled);
  const setMode = useFloorEngineStore((s) => s.setMode);
  const setGridEnabled = useFloorEngineStore((s) => s.setGridEnabled);
  const setSnapEnabled = useFloorEngineStore((s) => s.setSnapEnabled);

  const mod = modKeyLabel();

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      // style, not className: RN-web's ScrollView keeps its default
      // flexGrow:1 unless overridden on the OUTER container — the className
      // path doesn't land there, so a class-only grow-0 lets the toolbar
      // steal half the canvas area on web.
      style={styles.bar}
      contentContainerClassName="flex-row items-center gap-1.5 px-3 py-2">
      {MODE_ORDER.map((entry) => {
        if (
          !canModify &&
          entry.id !== FLOOR_MODE.VIEW &&
          entry.id !== FLOOR_MODE.PAN
        ) {
          return null;
        }
        return (
          <FloorPlanToolButton
            key={entry.id}
            icon={entry.icon}
            // i18n-used: pages:floorPlanEditor.modesObj.*
            label={t(`floorPlanEditor.modesObj.${entry.id}`, { ns: 'pages' })}
            {...(entry.hint !== undefined ? { hint: entry.hint } : {})}
            onPress={() => setMode(entry.id)}
            {...(mode === entry.id ? { highlight: 'solid' as const } : {})}
          />
        );
      })}

      {canModify ? (
        <>
          <View className="bg-border mx-1 h-6 w-px" />
          <FloorPlanToolButton
            icon={Undo2}
            label={t('common:undo')}
            hint={`${mod}Z`}
            disabled={!canUndo}
            onPress={onUndo}
          />
          <FloorPlanToolButton
            icon={Redo2}
            label={t('common:redo')}
            hint={`${mod}⇧Z`}
            disabled={!canRedo}
            onPress={onRedo}
          />
          <FloorPlanToolButton
            icon={Copy}
            label={t('common:duplicate')}
            hint={`${mod}D`}
            disabled={!hasSelection}
            onPress={onDuplicate}
          />
          <FloorPlanToolButton
            icon={Trash2}
            label={t('common:delete')}
            hint={`Del`}
            disabled={!hasSelection}
            onPress={onDelete}
            iconClassName="text-destructive"
          />

          <View className="bg-border mx-1 h-6 w-px" />
          <FloorPlanToolButton
            icon={Grid3x3}
            label={t('pages:floorPlanEditor.toggleGrid')}
            onPress={() => setGridEnabled(!gridEnabled)}
            {...(gridEnabled ? { highlight: 'soft' as const } : {})}
          />
          <FloorPlanToolButton
            icon={Magnet}
            label={t('pages:floorPlanEditor.toggleSnap')}
            onPress={() => setSnapEnabled(!snapEnabled)}
            {...(snapEnabled ? { highlight: 'soft' as const } : {})}
          />
          <FloorPlanToolButton
            icon={Ruler}
            label={t('fields:floorPlanDimensions.label')}
            onPress={onRoomSize}
          />

          <View className="bg-border mx-1 h-6 w-px" />
          <FloorPlanToolButton
            icon={Save}
            label={t('common:save')}
            onPress={onSave}
            highlight="solid"
          />
        </>
      ) : null}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  bar: { flexGrow: 0, flexShrink: 0 },
});

export default FloorPlanEditorToolbar;
